"use client";

import { useEffect, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { signal } from "@preact/signals-react";
import { useSignals } from "@preact/signals-react/runtime";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";

interface EventImageCarouselProps {
  mainImage: string;
  eventImages?: string[];
  alt: string;
}

const currentImageIndex = signal(0);
const isAutoPlaying = signal(true);

export function EventImageCarousel({ mainImage, eventImages, alt }: EventImageCarouselProps) {
  useSignals();
  const autoplayRef = useRef(
    Autoplay({ delay: 5000, stopOnInteraction: false, stopOnMouseEnter: true })
  );

  // Combine and sanitize image list (filter out empty/invalid URLs)
  const allImages = [
    // Ensure mainImage is valid
    ...(mainImage && typeof mainImage === "string" && mainImage.trim().length > 0 ? [mainImage] : []),
    // Filter eventImages array
    ...(Array.isArray(eventImages) ? eventImages : [])
  ].filter((src): src is string => 
    src && typeof src === "string" && src.trim().length > 0 && src !== "null" && src !== "undefined"
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: allImages.length > 1,
      // Adjust slide transition speed (higher is slower). Defaults are fine too
      // speed: 10,
      align: "start",
      skipSnaps: false,
    },
    [autoplayRef.current]
  );
  
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    currentImageIndex.value = emblaApi.selectedScrollSnap();
  }, [emblaApi]);

  const goToImage = (index: number) => {
    if (!emblaApi) return;
    emblaApi.scrollTo(index);
  };
  
  const goToPrevious = () => {
    emblaApi?.scrollPrev();
  };
  
  const goToNext = () => {
    emblaApi?.scrollNext();
  };
  
  const toggleAutoPlay = () => {
    isAutoPlaying.value = !isAutoPlaying.value;
    if (isAutoPlaying.value) {
      autoplayRef.current?.play?.();
    } else {
      autoplayRef.current?.stop?.();
    }
  };
  
  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);

    // Ensure autoplay state reflects the toggle
    if (isAutoPlaying.value) {
      autoplayRef.current?.play?.();
    } else {
      autoplayRef.current?.stop?.();
    }

    return () => {
      emblaApi?.off("select", onSelect);
      emblaApi?.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);
  
  // Re-initialize when image list changes
  useEffect(() => {
    currentImageIndex.value = 0;
    emblaApi?.reInit();
  }, [emblaApi, mainImage, eventImages]);
  
  if (allImages.length === 0) {
    return (
      <div className="relative">
        <div className="w-full h-[180px] md:h-[280px] lg:h-[380px] rounded-xl mb-6 bg-gray-200 flex items-center justify-center">
          <span className="text-gray-500">No image available</span>
        </div>
      </div>
    );
  }

  if (allImages.length === 1) {
    return (
      <div className="relative">
        <img
          src={allImages[0]}
          alt={alt}
          className="w-full h-[180px] md:h-[280px] lg:h-[380px] object-cover rounded-xl mb-6"
        />
      </div>
    );
  }
  
  return (
    <div className="relative group">
      {/* Swipe/Slide Carousel */}
      <div className="overflow-hidden rounded-xl mb-6" ref={emblaRef}>
        <div className="flex">
          {allImages.map((src, index) => (
            <div key={index} className="min-w-0 flex-[0_0_100%]">
              <img
                src={src}
                alt={`${alt} - Image ${index + 1}`}
                className="w-full h-[180px] md:h-[280px] lg:h-[380px] object-cover"
                draggable={false}
              />
            </div>
          ))}
        </div>
      </div>
      
      {/* Navigation Arrows */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
        onClick={goToPrevious}
      >
        <ChevronLeft className="h-5 w-5" />
      </Button>
      
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
        onClick={goToNext}
      >
        <ChevronRight className="h-5 w-5" />
      </Button>
      
      {/* Image Indicators */}
      {allImages.length > 1 && (
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {allImages.map((_, index) => (
            <button
              key={index}
              onClick={() => goToImage(index)}
              className={`w-2 h-2 rounded-full transition-all duration-200 ${
                index === currentImageIndex.value
                  ? "bg-white scale-125"
                  : "bg-white/50 hover:bg-white/75"
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}
      
      {/* Auto-play Toggle */}
      {allImages.length > 1 && (
        <Button
          variant="ghost"
          size="sm"
          className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          onClick={toggleAutoPlay}
        >
          {isAutoPlaying.value ? "Pause" : "Play"}
        </Button>
      )}
      
      {/* Image Counter */}
      {allImages.length > 1 && (
        <div className="absolute top-4 left-4 bg-black/50 text-white px-2 py-1 rounded-lg text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          {currentImageIndex.value + 1} / {allImages.length}
        </div>
      )}
    </div>
  );
}
